import React, { useEffect, useState } from 'react';
import {
  Platform,
  ScrollView, StyleSheet, View,
} from 'react-native';
import TicketGroupTag from './TicketGroupTag';
import updateTicketUserGroups from '../../../requests/Tickets/UpdateTicketUserGroups';
import { UserGroup } from '../../../constants/Types';
import Colors from '../../../constants/Colors';
import Style from '../../../constants/Style';
import PressableStyled from '../../../components/PressableStyled';
import PlusIcon from '../../../components/16pxIcons/PlusIcon';
import AddGroupList from '../../../components/AssetManagement/AddTemplateModal/components/IssueList/components/IssueRow/components/AddGroupList';
import SelectionModal from '../../../components/SelectionModal';

interface TicketUserGroupsProps {
  ticketId: string,
  userGroups: UserGroup[],
  ticketUserGroupIds: string[],
  // eslint-disable-next-line no-unused-vars
  setSavingTicketUserGroups: (saving: boolean) => void,
  onUpdateTicketUserGroups?: () => void,
  ticketResolved?: boolean,
}

const TicketUserGroups = ({
  ticketId,
  userGroups,
  ticketUserGroupIds,
  setSavingTicketUserGroups,
  onUpdateTicketUserGroups,
  ticketResolved,
}: TicketUserGroupsProps) => {
  const [showAddTagList, setShowAddTagList] = useState<boolean>(false);
  const [tags, setTags] = useState<string[]>(ticketUserGroupIds ?? []);
  const tagsEditable = !ticketResolved;

  useEffect(() => {
    setTags(ticketUserGroupIds ?? []);
  }, [ticketUserGroupIds]);

  useEffect(() => {
    setShowAddTagList(false);
  }, [ticketId]);

  useEffect(() => {
    if (!tagsEditable) setShowAddTagList(false);
  }, [tagsEditable]);

  const updateTags = (updatedTags: string[]) => {
    if (!tagsEditable) return;
    setSavingTicketUserGroups(true);
    updateTicketUserGroups(ticketId, updatedTags).then(() => {
      setTags(updatedTags);
      onUpdateTicketUserGroups?.();
      setShowAddTagList(false);
      setSavingTicketUserGroups(false);
    });
  };

  const sortedUserGroups: UserGroup[] = [...(userGroups ?? [])]
    .sort((prev: UserGroup, next: UserGroup) => (prev?.Name ?? '')
      .toLowerCase().localeCompare((next?.Name ?? '').toLowerCase()));

  const sortedTags: string[] = [...(tags ?? [])]
    .filter((tag) => !!tag)
    .sort((prevGroupId: string, nextGroupId: string) => {
      const prevName = sortedUserGroups
        .find((group: UserGroup) => group.UserGroupId === prevGroupId)?.Name ?? prevGroupId;
      const nextName = sortedUserGroups
        .find((group: UserGroup) => group.UserGroupId === nextGroupId)?.Name ?? nextGroupId;
      return prevName.toLowerCase().localeCompare(nextName.toLowerCase());
    });

  return <View style={{
    flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 6,
  }}>
    {sortedTags.length > 0 && <>
      {sortedTags.map((groupId: string, index: number) => (
        <TicketGroupTag
          key={`${groupId}-${index}`}
          groupId={groupId}
          userGroups={sortedUserGroups}
          deleteAction={(tagsEditable && tags?.length > 1) ? () => {
            updateTags((tags ?? []).filter((id: string) => id !== groupId));
          } : null}
          tags={tags}
          itemIndex={index}
          addGroup={updateTags}
        />
      ))}
    </>}
    <View>
      {tagsEditable ? <PressableStyled
        style={[Style.row, {
          borderWidth: 1,
          borderColor: Colors.light.hereworksDarkBlue,
          borderRadius: 6,
          padding: 4,
          zIndex: 1,
        }]}
        disabled={!tagsEditable || tags?.length >= userGroups?.length}
        onPress={() => {
          if (!tagsEditable) return;
          setShowAddTagList(!showAddTagList);
        }}>
        <PlusIcon color={Colors.light.hereworksDarkBlue} />
      </PressableStyled> : null}
      {showAddTagList && Platform.OS === 'web' && <ScrollView
        style={styles.tagDropdown}
        contentContainerStyle={styles.tagDropdownContent}
        nestedScrollEnabled
        scrollEnabled
        keyboardShouldPersistTaps='handled'
        showsVerticalScrollIndicator
      >
        <AddGroupList
          {...{ tags }}
          update={(userGroupId: string) => {
            updateTags([...(tags ?? []), userGroupId]);
          }}
          groups={sortedUserGroups}
        />
      </ScrollView>}
      {Platform.OS !== 'web' && <SelectionModal
        title='Add user group'
        isVisible={showAddTagList}
        hide={() => setShowAddTagList(false)}
        listContent={<AddGroupList
          {...{ tags }}
          update={(userGroupId: string) => {
            updateTags([...(tags ?? []), userGroupId]);
          }}
          groups={sortedUserGroups}
        />}
      />}
    </ View>
  </View>;
};

export default TicketUserGroups;

const styles = StyleSheet.create({
  addUserGroupTitle: {
    color: Colors.light.hereworksDarkBlue,
    fontSize: 12,
    fontFamily: 'Roboto_400Regular',
    marginRight: 6,
  },
  tagDropdown: {
    position: 'absolute',
    right: 0,
    top: 38,
    marginTop: 4,
    maxHeight: 180,
    minWidth: 220,
    alignSelf: 'flex-start',
    zIndex: 20,
    backgroundColor: Colors.light.plainBackground,
    borderWidth: 1,
    borderColor: Colors.light.hereworksDarkBlue,
    borderRadius: 6,
  },
  tagDropdownContent: {
    flexGrow: 1,
  },
});
